<template>
  <div
    ref="textLabelRef"
    class="text-label absolute"
    :class="[{ editable: isEditMode }, 'pointer-events-none']"
    :style="{ transform: label.transform, visibility: label.visible ? 'visible' : 'hidden', fontSize: `${label.textSize}px` }"
    data-testid="text-label"
    @keydown.stop=""
  >
    <InlineEditable
      class="dark-3d-labels bg-black rounded-none"
      theme="dark-3d-labels"
      data-threed-entity="text-label"
      :value="editingContent"
      :data-labelid="label.id"
      :class="{ focus: focused, selected, 'pointer-events-auto': visible && isEditMode }"
      allow-line-break
      :focus-on-mount="selected && !label.content.length && isEditMode"
      :placeholder="'Insert text here\u2026'"
      data-testid="label-input"
      no-blur-element=".label-controls"
      :style="{ transform: label.offset }"
      @input="updateLocalLabel"
      @save="save"
      @no-blur-save="noBlurSave"
      @clear="save"
      @focus="onFocus"
      @cancel="cancel"
      @click.stop=""
    />
  </div>
</template>

<script lang="ts" setup>
import type { DeepReadonly } from 'vue'
import type { LabelRenderData } from '@/lib/three-d-resources/VueRefRenderer'
import { Vector3 } from 'three'
import type { LabelsManager } from '~/lib/three-d-resources/labels-manager'
import { throttle } from '@/lib/utils'

const props = defineProps({
  label: {
    type: Object as PropType<DeepReadonly<LabelRenderData>>,
    required: true,
  },
  labelsManager: {
    type: Object as PropType<LabelsManager>,
    required: true,
  },
  selected: {
    type: Boolean,
    default: false,
  },
  visible: {
    type: Boolean,
    default: false, // To avoid initial load flash it makes more sense to the default to be false
  },
  isModelTweening: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['resize', 'deselect'])

const focused = ref(false)
const editingContent = ref(props.label.content)
const isEditMode = computed(() => props.labelsManager.mode.value === 'edit')
//
const tweeningTransition = ref(props.isModelTweening)
const textLabelRef = ref<HTMLElement>()
//
async function noBlurSave() {
  const { label, labelsManager } = props

  labelsManager.updateLabel(label.id, {
    content: editingContent.value.trim(),
    refreshKey: label.refreshKey,
  })
}

async function save() {
  const { label, labelsManager } = props

  labelsManager.updateLabel(label.id, {
    content: editingContent.value.trim(),
    isEditing: false,
    isNewlyCreated: false,
  })

  focused.value = false
  emit('deselect')
}

function updateLocalLabel(newContent: string) {
  if (focused.value) {
    editingContent.value = newContent
  }
}

async function onFocus() {
  focused.value = true
}

const opacity = computed(() => {
  if (!props.visible) return '0'
  return '100%'
})

let observer: ResizeObserver | undefined
onMounted(() => {
  observer = new ResizeObserver(payload => {
    const [{ target: div }] = payload
    if (div instanceof HTMLElement) {
      emit('resize', {
        width: div.offsetWidth,
        height: div.offsetHeight,
      })
    }
  })
})

watch(
  textLabelRef,
  newTextLabelRef => {
    if (!newTextLabelRef) return

    observer?.observe(newTextLabelRef)
    emit('resize', { width: newTextLabelRef.offsetWidth, height: newTextLabelRef.offsetHeight })
  },
  { immediate: true }
)

onUnmounted(() => {
  observer?.disconnect()
})

watch(() => props.label.content, _resetContents)

let timeout: NodeJS.Timeout
watch(
  () => props.isModelTweening,
  throttle((isTweening: boolean) => {
    tweeningTransition.value = isTweening

    clearTimeout(timeout)
    // Set back to false in all cases after 3000ms transition. Throttle
    // will not catch any new events in the 100ms timeframe.
    timeout = setTimeout(() => (tweeningTransition.value = false), 3000)
  }, 100),
  { immediate: true }
)

async function cancel({ explicit }: { explicit: boolean }) {
  if (!props.label.isNewlyCreated) {
    if (explicit) {
      _resetContents()
    }
    return
  }

  props.labelsManager.removeLabel(props.label.id)
}

function _resetContents() {
  editingContent.value = props.label.content
}
</script>

<style lang="stylus" scoped>
.text-label
  display flex
  white-space nowrap
  // use ems instead of rems to be relative to label font size
  line-height 1.5em
  transform-origin: 0 0 0
:deep(.ui-inline-editable)
  // use ems instead of rems to be relative to label font size
  padding 0.25em 0.5em
  cursor inherit
</style>
